import _ from "lodash";
import { HelpCircleIcon, Loader } from "lucide-react";
import { useCallback } from "react";
import Skeleton from "src/components/Skeleton";
import Tooltip from "src/components/Tooltip";
import { MultiSelect } from "src/components/ui/multi-select";
import { useApplication } from "src/hooks/useApplication";
import { Logger } from "src/log";
import { cn } from "src/utilities/shadcnUtils";

const logger = new Logger("ModifiableKeyValue");

export const ModifiableKeyMultiSelect = ({
  label,
  loading,
  appId,
  path,
  options,
  formatValue,
  formatLabel,
  mutateValueBeforeUpdate,
  className,
  tooltip,
  defaultValues,
}: {
  label: string;
  loading?: boolean;
  appId?: string;
  path: string;
  options: { value: string; label: string }[];
  formatValue?: (val: any) => string;
  formatLabel?: (val: string) => string;
  mutateValueBeforeUpdate?: (val: string) => any;
  className?: string;
  tooltip?: string;
  defaultValues?: string[];
}) => {
  const {
    updateApplication,
    updateApplicationProperty,
    upsertApplicationSolicitor,
    data: application,
    isPending,
  } = useApplication({
    id: appId,
    initialRefetch: false,
    cached: true,
  });

  const value = _.get(application, path);

  const handleValueChange = useCallback(
    (newValues: string[]) => {
      let newValue: string[] | null = newValues.map((v) => {
        if (mutateValueBeforeUpdate) {
          return mutateValueBeforeUpdate(v);
        }
        if (v === "") {
          return null;
        }
        return v;
      });

      if (newValue === value) {
        return;
      }

      if (newValue.length === 0) {
        newValue = null;
      }

      if (path.startsWith("property.")) {
        logger.log(`Setting property ${path}: ${newValue}`);
        updateApplicationProperty({ [path]: newValue });
      } else if (path.startsWith("solicitor.")) {
        const withoutPrefix = path.replace("solicitor.", "");
        logger.log(`Setting solicitor ${path} (${withoutPrefix}): ${newValue}`);
        upsertApplicationSolicitor.mutateAsync({ [withoutPrefix]: newValue });
      } else {
        logger.log(`Setting application ${path}: ${newValue}`);
        updateApplication({ [path]: newValue });
      }
    },
    [value, path, mutateValueBeforeUpdate]
  );

  let fmtValue = value;
  if (formatValue && value) {
    fmtValue = value.map((v: string) => formatValue(v));
  }

  return (
    <div className={cn("flex flex-row flex-wrap gap-1 items-center justify-between p-3 rounded", className)}>
      <div className="flex flex-row mr-2">
        <p className="text-sm font-bold flex-1">{label}:</p>
        {tooltip ? (
          <Tooltip white text={tooltip}>
            <HelpCircleIcon className="ml-1" size={12} />
          </Tooltip>
        ) : null}
      </div>

      {loading ? (
        <Skeleton width={160} />
      ) : (
        <div className="flex flex-row max-w-[300px]">
          <div className="relative flex flex-row max-w-full">
            <MultiSelect
              options={options}
              values={fmtValue}
              suffixIcon={
                isPending ? <Loader className="w-4 h-4 text-gray-600 animate-spin shrink-0" /> : undefined
              }
              onChange={handleValueChange}
              defaultValues={defaultValues}
              className={cn(
                "text-right text-ellipsis line-clamp-2 rounded-md min-w-[120px] min-w-auto max-w-full"
              )}
              buttonClassName="bg-gray-400 hover:bg-gray-400 max-w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
};
